import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import type { SaveStructuredResultInput } from '@lims/shared-validation';
import { useTranslation } from 'react-i18next';
import {
  COARSE_AGGREGATE_DISPLAY_PLACES,
  D2041_MAX_DETERMINATIONS,
  D2041_READING_FIELDS,
  D2726_MAX_SPECIMENS,
  D2726_READING_FIELDS,
  RESULT_TEMPLATE,
  SPECIFIC_GRAVITY_DISPLAY_PLACES,
  T85_MAX_DETERMINATIONS,
  T85_READING_FIELDS,
  averageSpecificGravity,
  averageT85,
  evaluateD2041Determination,
  evaluateD2726Specimen,
  evaluateT85Determination,
  formatDecimalForDisplay,
  isD2041Document,
  isD2726Document,
  isT85Document,
  type StructuredDocument,
  type T85Calculation,
} from '@lims/shared-types';
import { ApiError } from '../../api/client.js';
import { attemptsApi, type Attempt } from '../../api/endpoints.js';
import {
  ActionButton,
  Alert,
  DateTimeField,
  Panel,
  Reading,
  TextAreaField,
  TextField,
  useToast,
  fieldGridClass,
} from '../../components/ui/index.js';
import { useSaveFailureFocus } from '../../components/ui/server-form.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import {
  AttemptHeaderPanel,
  ENTERED_MARK,
  ProvenanceLegend,
  rolesCovered,
  type StructuredFormHandle,
} from './D2216Form.js';
import {
  BeforeSubmitPanel,
  BenchLayout,
  BenchSection,
  CardStateLabel,
  DeterminationCard,
  LiveResultsPanel,
  SectionStrip,
  type BenchStep,
  type CardState,
  type CheckItem,
  type LiveValue,
  type SectionState,
} from './bench-parts.js';
import { ResultBlock, SummaryResult } from './specialized-form/index.js';

/**
 * ASTM D2726 (Gmb), ASTM D2041 (Gmm) AND AASHTO T85 (specific gravity and
 * absorption of coarse aggregate) — the templates whose result is a few rows
 * of three masses (decisions D260–D266, D267–D274). One form serves all three:
 * each test is a CONFIGURATION (its fields, its readings, its calculation and
 * its labels), every one traced to its workbook — `asphalt.xlsx` sheet `2`,
 * the aggregate workbooks' sheet `3`. Not a formula engine: each calculation
 * is the shared function written for that test.
 *
 * The rules are D2216's (D237–D250): entered readings look different from
 * calculated values; a calculation shown while typing is a PREVIEW by the
 * same function the server runs, and the server's stored values replace it
 * once saved; A FAILED SAVE KEEPS EVERYTHING TYPED — the form's state is
 * replaced only by a successful save's answer, never by an error.
 */

type Calculation = Readonly<Record<string, string>>;

interface Evaluation {
  readonly issues: readonly { readonly field: string; readonly code: string }[];
  readonly calculation: Calculation | null;
}

interface StoredRow {
  readonly id: string;
  readonly specimenNumber?: string | null;
  readonly calculation: Calculation | null;
  readonly [field: string]: unknown;
}

type Summary = Readonly<Record<string, string | null>>;

/** One average the template shows: its key in the summary, its places and its label. */
interface AverageConfig {
  readonly key: string;
  readonly places: number;
  /** The full i18n key of its label. */
  readonly label: string;
}

export interface TemplateConfig {
  /** The i18n namespace and test id prefix. */
  readonly ns: 'd2726' | 'd2041' | 't85';
  /** Where the rows live in the document and the API body. */
  readonly rowsField: 'specimens' | 'determinations';
  readonly maxRows: number;
  /** Text fields of the test information, as the workbook holds them. */
  readonly infoFields: readonly string[];
  /** The i18n key describing those fields. */
  readonly infoHint: string | null;
  /** Whether each row carries a specimen number (the asphalt sheet's; T85 has none). */
  readonly numberField: boolean;
  /** Whether the workbook records the ambient pair (the T85 sheet does not, D272). */
  readonly environment: boolean;
  readonly readingFields: readonly string[];
  /** The calculated values of one row: key, display places, emphasised. */
  readonly calculated: readonly (readonly [string, number, boolean])[];
  readonly averages: readonly AverageConfig[];
  readonly evaluate: (row: Readonly<Record<string, string | null>>) => Evaluation;
  readonly accepts: (document: StructuredDocument | null | undefined) => boolean;
  /** The server's averages, from the stored document. */
  readonly storedSummary: (document: StructuredDocument) => Summary;
  /** The same averages over rows calculated on screen — the server's function. */
  readonly previewSummary: (calculations: readonly (Calculation | null)[]) => Summary;
}

const GRAVITY = SPECIFIC_GRAVITY_DISPLAY_PLACES.specificGravity;

export const D2726_FORM: TemplateConfig = {
  ns: 'd2726',
  rowsField: 'specimens',
  maxRows: D2726_MAX_SPECIMENS,
  infoFields: ['methodUsed'],
  infoHint: 'd2726.methodUsedHint',
  numberField: true,
  environment: true,
  readingFields: D2726_READING_FIELDS,
  calculated: [
    ['volume', SPECIFIC_GRAVITY_DISPLAY_PLACES.volume, false],
    ['bulkSpecificGravity', GRAVITY, true],
  ],
  averages: [{ key: 'averageBulkSpecificGravity', places: GRAVITY, label: 'd2726.average' }],
  evaluate: (row) =>
    evaluateD2726Specimen({
      dryMassInAir: row.dryMassInAir ?? null,
      ssdMassInAir: row.ssdMassInAir ?? null,
      massInWater: row.massInWater ?? null,
    }) as Evaluation,
  accepts: isD2726Document,
  storedSummary: (document) => (isD2726Document(document) ? document.summary : {}),
  previewSummary: (calculations) => ({
    averageBulkSpecificGravity: averageSpecificGravity(calculations.map((row) => row?.bulkSpecificGravity)),
  }),
};

export const D2041_FORM: TemplateConfig = {
  ns: 'd2041',
  rowsField: 'determinations',
  maxRows: D2041_MAX_DETERMINATIONS,
  infoFields: ['containerType', 'procedure'],
  infoHint: 'd2041.textHint',
  numberField: true,
  environment: true,
  readingFields: D2041_READING_FIELDS,
  calculated: [['maximumSpecificGravity', GRAVITY, true]],
  averages: [{ key: 'averageMaximumSpecificGravity', places: GRAVITY, label: 'd2041.average' }],
  evaluate: (row) =>
    evaluateD2041Determination({
      dryMassInAir: row.dryMassInAir ?? null,
      containerWaterMass: row.containerWaterMass ?? null,
      containerSampleWaterMass: row.containerSampleWaterMass ?? null,
    }) as Evaluation,
  accepts: isD2041Document,
  storedSummary: (document) => (isD2041Document(document) ? document.summary : {}),
  previewSummary: (calculations) => ({
    averageMaximumSpecificGravity: averageSpecificGravity(calculations.map((row) => row?.maximumSpecificGravity)),
  }),
};

const ABSORPTION = COARSE_AGGREGATE_DISPLAY_PLACES.absorption;
const AGGREGATE_GRAVITY = COARSE_AGGREGATE_DISPLAY_PLACES.specificGravity;

export const T85_FORM: TemplateConfig = {
  ns: 't85',
  rowsField: 'determinations',
  maxRows: T85_MAX_DETERMINATIONS,
  infoFields: [],
  infoHint: null,
  numberField: false,
  environment: false,
  readingFields: T85_READING_FIELDS,
  calculated: [
    ['absorptionPercent', ABSORPTION, true],
    ['bulkSpecificGravitySsd', AGGREGATE_GRAVITY, true],
    ['bulkSpecificGravityOd', AGGREGATE_GRAVITY, true],
    ['apparentSpecificGravity', AGGREGATE_GRAVITY, true],
  ],
  averages: [
    { key: 'averageAbsorptionPercent', places: ABSORPTION, label: 't85.average.averageAbsorptionPercent' },
    { key: 'averageBulkSpecificGravitySsd', places: AGGREGATE_GRAVITY, label: 't85.average.averageBulkSpecificGravitySsd' },
    { key: 'averageBulkSpecificGravityOd', places: AGGREGATE_GRAVITY, label: 't85.average.averageBulkSpecificGravityOd' },
    { key: 'averageApparentSpecificGravity', places: AGGREGATE_GRAVITY, label: 't85.average.averageApparentSpecificGravity' },
  ],
  evaluate: (row) =>
    evaluateT85Determination({
      dryMassInAir: row.dryMassInAir ?? null,
      ssdMassInAir: row.ssdMassInAir ?? null,
      massInWater: row.massInWater ?? null,
    }) as Evaluation,
  accepts: isT85Document,
  storedSummary: (document) => (isT85Document(document) ? { ...document.summary } : {}),
  previewSummary: (calculations) => ({ ...averageT85(calculations as readonly (T85Calculation | null)[]) }),
};

/** The form for a template key, or null when the key has no such form. */
export function specificGravityFormFor(key: string | null | undefined): TemplateConfig | null {
  if (key === RESULT_TEMPLATE.ASTM_D2726_V1) return D2726_FORM;
  if (key === RESULT_TEMPLATE.ASTM_D2041_V1) return D2041_FORM;
  if (key === RESULT_TEMPLATE.AASHTO_T85_V1) return T85_FORM;
  return null;
}

type RowDraft = Readonly<Record<string, string>> & { readonly id: string };

interface FormDraft {
  readonly info: Readonly<Record<string, string>>;
  readonly rows: readonly RowDraft[];
  readonly ambientTemperature: string;
  readonly relativeHumidity: string;
  readonly remarks: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
}

const text = (value: unknown) => (typeof value === 'string' ? value : '');
const orNull = (value: string | undefined) => (value === undefined || value.trim() === '' ? null : value);

function storedRows(config: TemplateConfig, document: StructuredDocument): readonly StoredRow[] {
  return ((document as unknown as Record<string, unknown>)[config.rowsField] ?? []) as readonly StoredRow[];
}

function draftFrom(config: TemplateConfig, attempt: Attempt, document: StructuredDocument): FormDraft {
  const info = (document as unknown as { testInformation?: Record<string, string | null> }).testInformation ?? {};
  const environment = 'environment' in document ? document.environment : null;
  return {
    info: Object.fromEntries(config.infoFields.map((field) => [field, text(info[field])])),
    rows: storedRows(config, document).map((row) => ({
      id: row.id,
      specimenNumber: text(row.specimenNumber),
      ...Object.fromEntries(config.readingFields.map((field) => [field, text(row[field])])),
    })),
    ambientTemperature: text(environment?.ambientTemperature),
    relativeHumidity: text(environment?.relativeHumidity),
    remarks: text(document.remarks),
    startedAt: attempt.generalResult.executionStartedAt,
    completedAt: attempt.generalResult.executionCompletedAt,
  };
}

/**
 * The form as the API takes it: blank is absent, readings exactly as typed.
 * A template sends only the parts its workbook records — each body is strict.
 */
function payloadOf(config: TemplateConfig, current: FormDraft): SaveStructuredResultInput {
  return {
    executionStartedAt: current.startedAt,
    executionCompletedAt: current.completedAt,
    ...(config.infoFields.length > 0
      ? { testInformation: Object.fromEntries(config.infoFields.map((field) => [field, orNull(current.info[field])])) }
      : {}),
    [config.rowsField]: current.rows.map((row) => ({
      id: row.id,
      ...(config.numberField ? { specimenNumber: orNull(row.specimenNumber) } : {}),
      ...Object.fromEntries(config.readingFields.map((field) => [field, orNull(row[field])])),
    })),
    ...(config.environment
      ? {
          environment: {
            ambientTemperature: orNull(current.ambientTemperature),
            relativeHumidity: orNull(current.relativeHumidity),
          },
        }
      : {}),
    remarks: orNull(current.remarks),
  } as unknown as SaveStructuredResultInput;
}

/** The stored row, when the draft row still holds exactly what was saved. */
function savedRow(config: TemplateConfig, row: RowDraft, stored: readonly StoredRow[]): StoredRow | null {
  const match = stored.find((candidate) => candidate.id === row.id);
  if (!match) return null;
  const same = config.readingFields.every((field) => text(match[field]) === (row[field] ?? '').trim());
  return same ? match : null;
}

/** The calculation a row shows: the server's when saved unchanged, otherwise the preview. */
function rowCalculation(config: TemplateConfig, row: RowDraft, stored: readonly StoredRow[]) {
  const saved = savedRow(config, row, stored);
  const preview = config.evaluate(row);
  const isServer = saved !== null && saved.calculation !== null;
  const calculation = isServer ? saved.calculation : preview.calculation;
  return { preview, isServer, calculation };
}

/** Whether the last refusal named a field under one of these prefixes. */
function namedUnder(errors: ReadonlyMap<string, string>, prefixes: readonly string[]): boolean {
  return [...errors.keys()].some((field) => prefixes.some((prefix) => field === prefix || field.startsWith(`${prefix}.`)));
}

export function SpecificGravityPanel({
  config,
  attempt,
  isEditable,
  onSaved,
  timeZone,
  handle,
  submitError,
  equipment,
  attachments,
  submitAction,
}: {
  readonly config: TemplateConfig;
  readonly attempt: Attempt;
  readonly isEditable: boolean;
  readonly onSaved: (next: Attempt) => Promise<void>;
  readonly timeZone: string | null;
  /** Filled by the panel so the page's Submit can read the current values. */
  readonly handle?: MutableRefObject<StructuredFormHandle | null>;
  /** A refused submission: its fields are marked here, and nothing typed is reset. */
  readonly submitError?: unknown;
  /** The page's equipment section, placed in the bench's section order. */
  readonly equipment?: ReactNode;
  /** The page's attachments, placed after the worksheet. */
  readonly attachments?: ReactNode;
  /** The page's Submit for review, placed beside Save draft. */
  readonly submitAction?: ReactNode;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const formRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const stored = attempt.structuredResult?.document;
  const document = config.accepts(stored) ? stored! : null;
  const [draft, setDraft] = useState<FormDraft | null>(() => (document ? draftFrom(config, attempt, document) : null));

  // Replaced ONLY when the stored document changes — after a successful save
  // or a reload. An error never resets what was typed.
  const storedStamp = attempt.structuredResult?.updatedAt ?? null;
  useEffect(() => {
    if (document) setDraft(draftFrom(config, attempt, document));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedStamp]);

  const save = useMutation({
    mutationFn: (current: FormDraft) => attemptsApi.saveStructured(attempt.id, payloadOf(config, current)),
    onSuccess: async (next) => {
      await onSaved(next);
      show({ tone: 'success', message: t('attempt.draftSaved') });
    },
  });

  if (handle) handle.current = { currentPayload: () => (draft ? payloadOf(config, draft) : null) };

  const [lastError, setLastError] = useState<unknown>(null);
  useEffect(() => {
    if (save.error) setLastError(save.error);
  }, [save.error]);
  useEffect(() => {
    if (submitError) setLastError(submitError);
  }, [submitError]);
  useEffect(() => {
    if (save.isSuccess) setLastError(null);
  }, [save.isSuccess]);

  useSaveFailureFocus(lastError, formRef, summaryRef);

  const fieldErrors = useMemo(() => {
    const errors = new Map<string, string>();
    if (lastError instanceof ApiError) {
      for (const detail of lastError.details) {
        if (detail.field && !errors.has(detail.field)) {
          errors.set(detail.field, t(`fieldErrors.${detail.code}`, { defaultValue: t('d2216.invalidValue') }));
        }
      }
    }
    return errors;
  }, [lastError, t]);

  if (!document || !draft) {
    return <Alert tone="danger" title={t('d2216.missingDocument')} />;
  }

  const ns = config.ns;
  const update = (patch: Partial<FormDraft>) => setDraft((current) => (current ? { ...current, ...patch } : current));
  const updateRow = (id: string, patch: Readonly<Record<string, string>>) =>
    update({ rows: draft.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)) });
  const errorOf = (field: string) => fieldErrors.get(field);
  const invalid = (field: string) => (errorOf(field) ? { isInvalid: true, error: errorOf(field)! } : {});

  const rowsStored = storedRows(config, document);
  // The averages shown: the server's, while every row is saved unchanged; a
  // preview over the rows calculated on screen otherwise.
  const allSaved = draft.rows.length === rowsStored.length && draft.rows.every((row) => savedRow(config, row, rowsStored));
  const summary = allSaved
    ? config.storedSummary(document)
    : config.previewSummary(draft.rows.map((row) => config.evaluate(row).calculation));
  const averageProvenance = allSaved ? t('d2216.calculatedByServer') : t('d2216.previewLabel');
  const averageReading = (average: AverageConfig) => (
    <Reading
      value={formatDecimalForDisplay(summary[average.key] ?? null, average.places)}
      provenance="calculated"
      provenanceLabel={averageProvenance}
      absentLabel={t('d2216.notCalculated')}
      emphasis
    />
  );

  // What the bench's strip, live results and checklist show — read from the
  // screen, never a rule of the test: the server decides on save and submit.
  const calculatedCount = draft.rows.filter((row) => rowCalculation(config, row, rowsStored).calculation !== null).length;
  const datesRecorded = Boolean(draft.startedAt && draft.completedAt);
  const instruments = rolesCovered(attempt);
  const rowsDone = draft.rows.length > 0 && calculatedCount === draft.rows.length;

  const steps: BenchStep[] = [
    {
      id: 'bench-info',
      label: t('bench.section.info'),
      state: datesRecorded && !namedUnder(fieldErrors, ['testInformation', 'executionStartedAt', 'executionCompletedAt']) ? 'done' : 'todo',
    },
    ...(equipment
      ? [
          {
            id: 'bench-equipment',
            label: t('bench.section.equipment'),
            state: (instruments.roles.length > 0 ? (instruments.covered ? 'done' : 'todo') : instruments.any ? 'done' : 'none') as SectionState,
          },
        ]
      : []),
    {
      id: 'bench-worksheet',
      label: t('bench.section.worksheet'),
      state: rowsDone && !namedUnder(fieldErrors, [config.rowsField]) ? 'done' : 'todo',
    },
    {
      id: 'bench-environment',
      label: config.environment ? t('bench.section.environment') : t('bench.section.remarks'),
      state: namedUnder(fieldErrors, ['environment', 'remarks'])
        ? 'todo'
        : config.environment && draft.ambientTemperature.trim() && draft.relativeHumidity.trim()
          ? 'done'
          : 'none',
    },
  ];

  const checks: CheckItem[] = [
    { key: 'dates', ok: datesRecorded, label: t('bench.check.dates') },
    ...(instruments.roles.length > 0 ? [{ key: 'instruments', ok: instruments.covered, label: t('bench.check.instruments') }] : []),
    {
      key: 'rows',
      ok: rowsDone,
      label: t('bench.check.rowsCalculated', { done: calculatedCount, total: draft.rows.length }),
    },
    ...(fieldErrors.size > 0 ? [{ key: 'refused', ok: false, label: t('bench.check.serverNamed', { count: fieldErrors.size }) }] : []),
  ];

  const live: LiveValue[] = config.averages.map((average) => ({
    key: average.key,
    label: t(average.label),
    value: averageReading(average),
  }));

  return (
    <BenchLayout
      strip={<SectionStrip steps={steps} />}
      main={
        <>
          <AttemptHeaderPanel attempt={attempt} timeZone={timeZone} />

          <div ref={formRef} className="flex flex-col gap-gutter" data-testid={`${ns}-form`}>
            <BenchSection id="bench-info">
              <Panel title={t('d2216.testInformationTitle')} description={t(`${ns}.testInformationHint`)}>
                <div className={fieldGridClass(2)}>
                  {config.infoFields.map((field) => (
                    <TextField
                      key={field}
                      label={t(`${ns}.${field}`)}
                      {...(config.infoHint ? { description: t(config.infoHint) } : {})}
                      value={draft.info[field] ?? ''}
                      onChange={(value) => update({ info: { ...draft.info, [field]: value } })}
                      isReadOnly={!isEditable}
                      {...invalid(`testInformation.${field}`)}
                    />
                  ))}
                  <DateTimeField
                    label={t('d2216.dateTested')}
                    value={draft.startedAt}
                    onChange={(value) => update({ startedAt: value })}
                    timeZone={timeZone}
                    isReadOnly={!isEditable}
                    {...invalid('executionStartedAt')}
                  />
                  <DateTimeField
                    label={t('attempt.executionCompletedLabel')}
                    value={draft.completedAt}
                    onChange={(value) => update({ completedAt: value })}
                    timeZone={timeZone}
                    isReadOnly={!isEditable}
                    {...invalid('executionCompletedAt')}
                  />
                </div>
              </Panel>
            </BenchSection>

            {equipment ? <BenchSection id="bench-equipment">{equipment}</BenchSection> : null}

            <BenchSection id="bench-worksheet">
              <Panel title={t(`${ns}.rowsTitle`)} description={t(`${ns}.rowsHint`)} actions={<ProvenanceLegend />}>
                {draft.rows.length === 0 ? (
                  <p className="text-body text-default-600" data-testid={`${ns}-no-rows`}>
                    {t(`${ns}.noRows`)}
                  </p>
                ) : (
                  <ol className="flex flex-col gap-stack">
                    {draft.rows.map((row, index) => (
                      <SpecimenRow
                        key={row.id}
                        config={config}
                        index={index}
                        row={row}
                        stored={rowsStored}
                        isEditable={isEditable}
                        onChange={(patch) => updateRow(row.id, patch)}
                        onRemove={() => update({ rows: draft.rows.filter((candidate) => candidate.id !== row.id) })}
                        errorOf={(field) => errorOf(`${config.rowsField}.${index}.${field}`)}
                      />
                    ))}
                  </ol>
                )}
                {errorOf(config.rowsField) ? (
                  <p className="mt-stack text-caption font-medium text-danger-700">{errorOf(config.rowsField)}</p>
                ) : null}
                <div className="mt-stack flex flex-wrap items-start gap-stack">
                  {isEditable ? (
                    <div className="flex flex-wrap items-center gap-inline">
                      <ActionButton
                        priority="secondary"
                        isDisabled={draft.rows.length >= config.maxRows}
                        onPress={() =>
                          update({
                            rows: [
                              ...draft.rows,
                              {
                                id: globalThis.crypto.randomUUID(),
                                ...(config.numberField ? { specimenNumber: '' } : {}),
                                ...Object.fromEntries(config.readingFields.map((field) => [field, ''])),
                              },
                            ],
                          })
                        }
                      >
                        {t(`${ns}.addRow`)}
                      </ActionButton>
                      {draft.rows.length >= config.maxRows ? (
                        <span className="text-caption text-default-600">{t(`${ns}.maxRows`, { max: config.maxRows })}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <SummaryResult testId={`${ns}-average`}>
                  {config.averages.map((average) => (
                    <ResultBlock key={average.key} label={t(average.label)} testId={`${ns}-${average.key}`}>
                      {averageReading(average)}
                    </ResultBlock>
                  ))}
                </SummaryResult>
                <p className="mt-1 text-caption text-default-600">{t(`${ns}.averageHint`)}</p>
              </Panel>
            </BenchSection>

            <BenchSection id="bench-environment">
              <Panel
                title={config.environment ? t('d2216.environmentTitle') : t('d2216.remarks')}
                {...(config.environment ? { description: t('d2216.environmentHint') } : {})}
              >
                {config.environment ? (
                  <div className={`${fieldGridClass(2)} mb-stack`}>
                    <TextField
                      label={t('d2216.ambientTemperature')}
                      value={draft.ambientTemperature}
                      onChange={(value) => update({ ambientTemperature: value })}
                      isReadOnly={!isEditable}
                      textDirection="ltr"
                      endContent={ENTERED_MARK}
                      {...invalid('environment.ambientTemperature')}
                    />
                    <TextField
                      label={t('d2216.relativeHumidity')}
                      value={draft.relativeHumidity}
                      onChange={(value) => update({ relativeHumidity: value })}
                      isReadOnly={!isEditable}
                      textDirection="ltr"
                      endContent={ENTERED_MARK}
                      {...invalid('environment.relativeHumidity')}
                    />
                  </div>
                ) : null}
                <div>
                  <TextAreaField
                    label={t('d2216.remarks')}
                    value={draft.remarks}
                    onChange={(value) => update({ remarks: value })}
                    isReadOnly={!isEditable}
                    rows={3}
                    {...invalid('remarks')}
                  />
                </div>
                <p className="mt-stack text-caption text-default-600">{t('attempt.emptyIsAbsent')}</p>
              </Panel>
            </BenchSection>
          </div>

          {attachments}
        </>
      }
      side={
        <>
          <LiveResultsPanel values={live} emptyLabel={t('bench.liveResultsEmpty')} />
          <BeforeSubmitPanel
            checks={checks}
            {...(isEditable
              ? {
                  actions: (
                    <>
                      <ActionButton priority="secondary" isLoading={save.isPending} onPress={() => save.mutate(draft)}>
                        {t('attempt.saveDraft')}
                      </ActionButton>
                      <span className="flex-1" />
                      {submitAction}
                    </>
                  ),
                }
              : {})}
            summary={
              <div ref={summaryRef} tabIndex={-1}>
                {save.error ? (
                  fieldErrors.size > 0 ? (
                    <Alert tone="danger" title={t('d2216.saveRefusedTitle')} description={t('d2216.saveRefusedBody')} />
                  ) : (
                    <ErrorNotice error={save.error} />
                  )
                ) : null}
              </div>
            }
          />
        </>
      }
    />
  );
}

function SpecimenRow({
  config,
  index,
  row,
  stored,
  isEditable,
  onChange,
  onRemove,
  errorOf,
}: {
  readonly config: TemplateConfig;
  readonly index: number;
  readonly row: RowDraft;
  readonly stored: readonly StoredRow[];
  readonly isEditable: boolean;
  readonly onChange: (patch: Readonly<Record<string, string>>) => void;
  readonly onRemove: () => void;
  readonly errorOf: (field: string) => string | undefined;
}) {
  const { t } = useTranslation();
  const ns = config.ns;
  const { preview, isServer, calculation } = rowCalculation(config, row, stored);
  const issues = preview.issues.filter((issue) => issue.code !== 'reading_required' && !errorOf(issue.field));
  const field = (name: string) => {
    const error = errorOf(name);
    return error ? { isInvalid: true, error } : {};
  };
  const source = calculation ? (isServer ? 'server' : 'preview') : 'none';
  const names = [...(config.numberField ? ['specimenNumber'] : []), ...config.readingFields];
  const hasError = names.some((name) => errorOf(name));
  const state: CardState = calculation ? 'calculated' : issues.length > 0 || hasError ? 'check' : 'incomplete';

  return (
    <DeterminationCard
      testId={`${ns}-row`}
      ariaLabel={t(`${ns}.rowNumbered`, { number: index + 1 })}
      title={t(`${ns}.rowNumbered`, { number: index + 1 })}
      state={<CardStateLabel state={state} label={t(`bench.card.${state}`)} />}
      {...(isEditable ? { onRemove, removeLabel: t(`${ns}.removeRow`) } : {})}
      entered={names.map((name) => (
        <TextField
          key={name}
          label={t(`${ns}.field.${name}`)}
          value={row[name] ?? ''}
          onChange={(value) => onChange({ [name]: value })}
          isReadOnly={!isEditable}
          textDirection="ltr"
          {...(name === 'specimenNumber' ? {} : { endContent: ENTERED_MARK })}
          {...field(name)}
        />
      ))}
      calculatedTestId={`${ns}-calculated`}
      calculatedSource={source}
      calculated={config.calculated.map(([key, places, emphasis]) => (
        <ResultBlock key={key} label={t(`${ns}.calculated.${key}`)} testId={`${ns}-${key}`}>
          <Reading
            value={calculation ? formatDecimalForDisplay(calculation[key] ?? null, places) : null}
            provenance="calculated"
            provenanceLabel={isServer ? t('d2216.calculatedByServer') : t('d2216.previewLabel')}
            absentLabel={t('d2216.notCalculated')}
            emphasis={emphasis}
          />
        </ResultBlock>
      ))}
      footer={
        <>
          <p className="text-caption text-default-600">
            {calculation ? (isServer ? t('d2216.calculatedByServer') : t('d2216.previewLabel')) : t(`${ns}.notCalculatedHint`)}
          </p>
          {issues.length > 0 ? (
            <ul className="mt-1 text-caption font-medium text-warning-700" data-testid={`${ns}-row-issues`}>
              {issues.map((issue) => (
                <li key={`${issue.field}-${issue.code}`}>
                  {t(`${ns}.field.${issue.field}`)}: {t(`fieldErrors.${issue.code}`)}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      }
    />
  );
}
